import { useCallback, useMemo, useRef } from "react";
import type { Task } from "@common/types/task";
import { Board } from "@design-system/Board/Board";
import type {
    BoardCardContext,
    BoardCollapsedChange,
    BoardLock,
} from "@design-system/Board/Board.types";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import { createIsPlacementShown } from "@design-system/Board/boardVisibility";
import { useTypedFlags } from "@hooks/useTypedFlags";
import type { TaskListCapabilities } from "../taskListCapabilities";
import type { TaskPlaceholderFilters } from "../taskPlaceholderFields";
import { TaskStatusTerminationConfirmationModal } from "../TaskStatusTerminationConfirmationModal";
import type { TaskBoardTask } from "../types";
import {
    TaskBoardBulkIsland,
    type TaskBoardIslandHandle,
} from "./TaskBoardBulkIsland";
import { TaskBoardCard } from "./TaskBoardCard";
import { TaskBoardCompactCard } from "./TaskBoardCompactCard";
import { TaskBoardDraftCard } from "./TaskBoardDraftCard";
import type { TaskBoardDisplayState } from "./taskBoardCache";
import { TASK_BOARD_DRAFT_ID } from "./taskBoardDraft";
import { ENTITY_LANE_BY } from "./taskBoardLanes";
import type { TaskCardEditField } from "./taskCardEdits";
import { useOpenTaskSection } from "../useOpenTaskSection";
import { useTaskBoardCardEdits } from "./useTaskBoardCardEdits";
import type { TaskBoardCollapseState } from "./useTaskBoardCollapse";
import { useTaskBoardCreate } from "./useTaskBoardCreate";
import { useTaskBoardGroupMove } from "./useTaskBoardGroupMove";
import type { TaskBoardModel } from "./useTaskBoardModel";
import { useTaskBoardMove } from "./useTaskBoardMove";
import { useTaskBoardSelection } from "./useTaskBoardSelection";
import { useTaskBoardShortcuts } from "./useTaskBoardShortcuts";
import styles from "./TasksBoard.module.scss";

const getTaskId = (task: TaskBoardTask) => task.id;
const getTaskTitle = (task: TaskBoardTask) => task.title;
const NO_SELECTION_ACTIONS: Partial<Record<TaskCardEditField, () => void>> = {};
const DEMO_TASK_LOCK: BoardLock = {};
const doNothing = () => undefined;

interface TasksBoardProps {
    tasks: readonly TaskBoardTask[];
    model: TaskBoardModel;
    display: Pick<
        TaskBoardDisplayState,
        "sort" | "cardFields" | "collapsedColumnIds" | "collapsedLaneIds"
    >;
    onCollapsedChange: (change: BoardCollapsedChange) => void;
    /** Cards a drop landed in these columns, so a collapsed one can expand. */
    onCardsDropped: TaskBoardCollapseState["onCardsDropped"];
    capabilities: Pick<
        TaskListCapabilities,
        "boardSelection" | "canCreateTasks"
    >;
    /** The page's filters, which a created task takes values from like List's. */
    filters: TaskPlaceholderFilters;
    isCrossWorkspace: boolean;
    onOpenTask: (task: Task) => void;
    /** The task that was just closed: its card takes focus back on mount. */
    focusTaskId: string | null;
}

/**
 * The Board layout: the page's filtered tasks in one column per group of the
 * board's column grouping, in the swimlanes the gear picks, ordered by the board
 * sort. Dropping a card in another column or lane updates the fields they
 * show; its chips and the card shortcuts edit the focused card. Selected
 * cards get the command island (bulk actions on a workspace, one card's
 * actions on the organization page) and a workspace board drags them as a
 * group. A column's `+`, the gap `+` between cards and N on a card create
 * a task where they are.
 */
export const TasksBoard = ({
    tasks,
    model,
    display: { sort, cardFields, collapsedColumnIds, collapsedLaneIds },
    onCollapsedChange,
    onCardsDropped,
    capabilities,
    filters,
    isCrossWorkspace,
    onOpenTask,
    focusTaskId,
}: Readonly<TasksBoardProps>) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const islandRef = useRef<TaskBoardIslandHandle>(null);
    const isMultiSelect = capabilities.boardSelection === "multiple";
    const { columns, lanes, hiddenFields } = model;
    const axisValues = useMemo(
        () => [...columns, ...(lanes ?? [])],
        [columns, lanes],
    );
    const { dragProps, confirmation } = useTaskBoardMove({
        tasks,
        axes: model.axes,
        axisValues,
        sort,
        onMoveApplied: onCardsDropped,
    });
    const isPlacementShown = useMemo(
        () =>
            createIsPlacementShown({
                columns,
                lanes,
                collapsedColumnIds,
                collapsedLaneIds,
            }),
        [columns, lanes, collapsedColumnIds, collapsedLaneIds],
    );
    const { editTask, enterTask, enteringItemIds } = useTaskBoardCardEdits({
        tasks,
        getPlacements: model.getPlacements,
        isPlacementShown,
    });
    const {
        selectedItemIds,
        selectedTasks,
        onSelectionChange,
        clearSelection,
    } = useTaskBoardSelection({
        tasks,
        getPlacements: model.getPlacements,
        isPlacementShown,
        mode: capabilities.boardSelection,
    });
    const hasIsland = isMultiSelect && selectedTasks.length > 1;
    const selectionActions = useMemo(
        () =>
            hasIsland
                ? {
                      status: () => islandRef.current?.setStatus(),
                      assignee: () => islandRef.current?.assignMember(),
                  }
                : NO_SELECTION_ACTIONS,
        [hasIsland],
    );
    const create = useTaskBoardCreate({
        tasks,
        model,
        sort,
        filters,
        canCreateTasks: capabilities.canCreateTasks,
        onCreated: enterTask,
        rootRef,
    });
    const { submitDraft, endDraft, returnDraftFocus, getDraftLock } = create;
    const { cardShortcuts, request, endShortcut } = useTaskBoardShortcuts({
        tasks,
        getPlacements: model.getPlacements,
        selectedItemIds,
        selectionActions,
    });
    const onMoveMany = useTaskBoardGroupMove({
        tasks,
        axes: model.axes,
        axisValues,
        sort,
        onMoveApplied: onCardsDropped,
    });
    const openTaskChat = useOpenTaskSection(onOpenTask, "chat");
    const openTaskDependencies = useOpenTaskSection(onOpenTask, "dependencies");
    const openTaskDetails = useOpenTaskSection(onOpenTask, "details");
    const { taskTitleInlineEdit } = useTypedFlags();

    const getLock = useCallback(
        (task: TaskBoardTask) =>
            isBusyClosesDemoId(task.id) ? DEMO_TASK_LOCK : getDraftLock(task),
        [getDraftLock],
    );

    const renderCard = useCallback(
        (task: TaskBoardTask, { placementKey }: BoardCardContext) => {
            if (task.id === TASK_BOARD_DRAFT_ID) {
                return (
                    <TaskBoardDraftCard
                        onCreate={submitDraft}
                        onEnd={endDraft}
                        onReturnFocus={returnDraftFocus}
                    />
                );
            }
            if (isBusyClosesDemoId(task.id)) {
                return (
                    <TaskBoardCompactCard
                        task={task}
                        cardFields={cardFields}
                        hiddenFields={hiddenFields}
                        isCrossWorkspace={isCrossWorkspace}
                        isOpen={false}
                        onOpen={doNothing}
                        onOpenDependencies={doNothing}
                    />
                );
            }
            return (
                <TaskBoardCard
                    task={task}
                    cardFields={cardFields}
                    hiddenFields={hiddenFields}
                    isCrossWorkspace={isCrossWorkspace}
                    onOpen={onOpenTask}
                    onEdit={editTask}
                    onOpenChat={openTaskChat}
                    onOpenDependencies={openTaskDependencies}
                    onEditDetails={openTaskDetails}
                    renameMode={taskTitleInlineEdit ? "inline" : "modal"}
                    shortcut={
                        request?.placementKey === placementKey ? request : null
                    }
                    onShortcutEnd={endShortcut}
                />
            );
        },
        [
            cardFields,
            hiddenFields,
            isCrossWorkspace,
            onOpenTask,
            editTask,
            openTaskChat,
            openTaskDependencies,
            openTaskDetails,
            taskTitleInlineEdit,
            request,
            endShortcut,
            submitDraft,
            endDraft,
            returnDraftFocus,
        ],
    );

    return (
        <div ref={rootRef} tabIndex={-1} className={styles.fill}>
            <Board
                aria-label="Tasks"
                items={create.items}
                columns={create.columns}
                lanes={lanes}
                hasCommandIsland={isMultiSelect}
                hasMultiValueLanes={model.laneBy === ENTITY_LANE_BY}
                getItemId={getTaskId}
                getItemLabel={getTaskTitle}
                getPlacements={create.getPlacements}
                renderCard={renderCard}
                compareItems={create.compareTasks}
                getLock={getLock}
                emptyColumnLabel="No tasks"
                collapsedColumnIds={collapsedColumnIds}
                collapsedLaneIds={collapsedLaneIds}
                onCollapsedChange={onCollapsedChange}
                enteringItemIds={enteringItemIds}
                cardShortcuts={cardShortcuts}
                selectedItemIds={selectedItemIds}
                onSelectionChange={onSelectionChange}
                {...dragProps}
                {...(isMultiSelect && { onMoveMany })}
                {...create.addProps}
                initialFocusedItemId={focusTaskId ?? undefined}
            />
            {isMultiSelect && (
                <div className={styles.island}>
                    <TaskBoardBulkIsland
                        ref={islandRef}
                        isShown={hasIsland}
                        selectedTasks={selectedTasks}
                        onClearSelection={clearSelection}
                    />
                </div>
            )}
            <TaskStatusTerminationConfirmationModal {...confirmation} />
        </div>
    );
};
